import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, AlertTriangle, X } from "lucide-react";
import { useUpload } from "../../context/UploadContext";
import { formatFileSize } from "../../utils/helpers";
import { encoderLabel } from "../../services/videoCompressionService";
import UploadStatusIcon from "../../components/common/UploadStatusIcon";
import { color } from "../../styles/theme";

const formatEta = (t, seconds) => {
  if (seconds == null) return "";
  if (seconds < 5) return t("runningUploads.eta.fewSeconds");
  if (seconds < 60) return t("runningUploads.eta.seconds", { seconds });
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs > 0 ? t("runningUploads.eta.minutesSeconds", { minutes: mins, seconds: secs }) : t("runningUploads.eta.minutes", { minutes: mins });
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? t("runningUploads.eta.hoursMinutes", { hours: hrs, minutes: remMins }) : t("runningUploads.eta.hours", { hours: hrs });
};

const formatMbps = (t, mbps) => {
  const n = Number(mbps || 0);
  if (!isFinite(n) || n <= 0) return "";
  return t("runningUploads.mbps", { value: n.toFixed(n >= 100 ? 0 : 2) });
};

const compressionStatusText = (t, item) => {
  const pct = Math.max(0, Math.min(100, Number(item.compressProgress || 0)));
  if (item.fileType?.startsWith("video/") && pct <= 30) {
    return t("runningUploads.status.copyingToStudio", { percent: pct });
  }
  return t("runningUploads.status.compressing", { percent: pct });
};

const statusText = (t, item) => {
  switch (item.status) {
    case "checking": return t("runningUploads.status.analysing");
    case "compressing": return compressionStatusText(t, item);
    case "uploading":
      if (item.uploadPhase === "handoff") {
        return t("runningUploads.status.preparingUpload", { percent: item.uploadProgress, mbps: formatMbps(t, item.uploadMbps) });
      }
      return t("runningUploads.status.uploading", { percent: item.uploadProgress, mbps: formatMbps(t, item.uploadMbps), eta: formatEta(t, item.eta) });
    case "retrying": return t("runningUploads.status.retrying");
    case "done": return item.duplicate ? t("runningUploads.status.doneSkipped") : t("runningUploads.status.done");
    case "failed": return item.error || t("runningUploads.status.failed");
    default: return t("runningUploads.status.queued");
  }
};

const RunningUploads = () => {
  const { t } = useTranslation(["uploads", "common"]);
  const { sessions, removeSession, cancelSession } = useUpload();
  const [confirmId, setConfirmId] = React.useState(null);
  const [expandedIds, setExpandedIds] = useState({}); // { [sessionId]: true }
  const fileListRefs = useRef({});

  const toggleExpand = (id) => setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  // Auto-scroll each session's file list to the active item
  useEffect(() => {
    Object.values(sessions).forEach((session) => {
      if (!expandedIds[session.id]) return;
      const container = fileListRefs.current[session.id];
      if (!container || !session.started) return;
      const activeItem = (session.fileItems || []).find((i) =>
        ["checking", "compressing", "uploading", "retrying"].includes(i.status)
      );
      if (!activeItem) return;
      const el = container.querySelector(`[data-file-id="${activeItem.id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  }, [sessions, expandedIds]);
  const sessionList = Object.values(sessions).reverse();

  if (sessionList.length === 0) {
    return (
      <div style={styles.empty}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, color: color.primary400 }}><UploadCloud size={36} strokeWidth={1.5} /></div>
        <h3 style={{ margin: "0 0 6px", color: "#666" }}>{t("runningUploads.emptyTitle")}</h3>
        <p style={{ margin: 0, color: "#999", fontSize: 13 }}>
          {t("runningUploads.emptyText")}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 style={styles.pageTitle}>{t("runningUploads.pageTitle")}</h2>
      {sessionList.map((session) => {
        const items = session.fileItems || [];
        const doneCount = items.filter((i) => i.status === "done").length;
        const failCount = items.filter((i) => i.status === "failed").length;
        const totalCount = items.length;
        const allDone = totalCount > 0 && items.every((i) => i.status === "done" || i.status === "failed");
        const isActive = session.started && items.some((i) => ["pending", "checking", "compressing", "uploading", "retrying"].includes(i.status));
        const overallProgress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;
        const activeItem = items.find((i) => ["checking", "compressing", "uploading", "retrying"].includes(i.status));

        return (
          <div key={session.id} style={styles.sessionCard}>
            {/* Cancel confirm dialog */}
            {confirmId === session.id && (
              <div style={styles.confirmOverlay}>
                <div style={styles.confirmBox}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, color: "#d97706" }}><AlertTriangle size={26} strokeWidth={1.75} /></div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{t("runningUploads.cancelUploadTitle")}</div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 16 }}>
                    {t("runningUploads.cancelUploadMessage")}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={styles.confirmKeepBtn} onClick={() => setConfirmId(null)}>{t("runningUploads.keepUploading")}</button>
                    <button style={styles.confirmCancelBtn} onClick={() => { cancelSession(session.id); setConfirmId(null); }}>{t("runningUploads.yesCancel")}</button>
                  </div>
                </div>
              </div>
            )}

            {/* Compact Header Row */}
            <div style={styles.sessionHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.sessionTitle}>
                  {session.gallery?.Name} / {session.album?.Name}
                </div>
                <div style={styles.sessionMeta}>
                  {t("runningUploads.filesCount", { done: doneCount, total: totalCount })}
                  {failCount > 0 && <span style={{ color: "#e53935" }}>{t("runningUploads.failedCount", { count: failCount })}</span>}
                  {allDone && <span style={{ color: "#16a34a", fontWeight: 600 }}>{t("runningUploads.complete")}</span>}
                  {isActive && !allDone && <span style={{ color: "#6c63ff", fontWeight: 600 }}>{t("runningUploads.uploadingLabel")}</span>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {isActive && (
                  <button style={styles.cancelBtn} onClick={() => setConfirmId(session.id)}>
                    <X size={12} strokeWidth={2.5} /> {t("runningUploads.cancel")}
                  </button>
                )}
                {allDone && (
                  <button style={styles.dismissBtn} onClick={() => removeSession(session.id)}>
                    {t("runningUploads.dismiss")}
                  </button>
                )}
              </div>
            </div>

            {/* Overall progress bar */}
            <div style={styles.overallBarWrap}>
              <div style={{
                ...styles.overallBarFill,
                width: `${overallProgress}%`,
                background: allDone ? "#16a34a" : "#6c63ff",
              }} />
            </div>

            {/* Current file mini-status (shown when collapsed) */}
            {activeItem && !expandedIds[session.id] && (
              <div style={styles.activeFileMini}>
                <span style={{ display: "inline-flex" }}><UploadStatusIcon status={activeItem.status} fileType={activeItem.fileType} isDuplicate={activeItem.duplicate} size={13} /></span>
                <span style={styles.activeFileName}>{activeItem.fileName}</span>
                <span style={styles.activeFileStatus}>{statusText(t, activeItem)}</span>
              </div>
            )}

            {/* Toggle details button */}
            <button
              style={styles.toggleBtn}
              onClick={() => toggleExpand(session.id)}
            >
              {expandedIds[session.id] ? t("runningUploads.hideDetails") : t("runningUploads.showDetails", { count: totalCount })}
            </button>

            {/* Collapsible File list */}
            {expandedIds[session.id] && (
              <div style={styles.fileList} ref={(el) => { fileListRefs.current[session.id] = el; }}>
                {items.map((item) => (
                  <div key={item.id} data-file-id={item.id} style={{
                    ...styles.fileRow,
                    background: item.status === "done" ? "#f0fdf4" : item.status === "failed" ? "#fef2f2" : "#fff",
                  }}>
                    <span style={styles.fileIcon}><UploadStatusIcon status={item.status} fileType={item.fileType} isDuplicate={item.duplicate} /></span>
                    <div style={styles.fileInfo}>
                      <span style={styles.fileName}>{item.fileName}</span>

                      {item.status === "compressing" && (
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.barFill, width: `${item.compressProgress}%`, background: "#f59e0b" }} />
                        </div>
                      )}
                      {item.status === "uploading" && (
                        <div style={styles.barWrap}>
                          <div style={{ ...styles.barFill, width: `${item.uploadProgress}%`, background: "#6c63ff" }} />
                        </div>
                      )}

                      <span style={{
                        ...styles.statusLabel,
                        color: item.status === "done" ? "#16a34a" : item.status === "failed" ? "#e53935" : "#888",
                      }}>
                        {statusText(t, item)}
                        {item.status === "pending" && item.queuePosition > 0 && (
                          <span style={{ color: "#aaa" }}>{t("runningUploads.queuePosition", { position: item.queuePosition })}</span>
                        )}
                      </span>
                                          {item.compressEngine && (
                                            <span style={{
                                              display: "inline-block",
                                              marginTop: 2,
                                              fontSize: 10,
                                              fontWeight: 600,
                                              color: item.compressEngine === "libx264" ? "#888" : "#6c63ff",
                                              background: item.compressEngine === "libx264" ? "#f3f4f6" : "#ede9fe",
                                              borderRadius: 4,
                                              padding: "1px 6px",
                                            }}>
                                              {encoderLabel(item.compressEngine)}
                                            </span>
                                          )}
                    </div>
                    <span style={item.originalSize ? styles.fileSizeCompressed : styles.fileSize}>
                      {item.originalSize
                        ? <>{formatFileSize(item.originalSize)}<span style={styles.sizeArrow}> → </span><span style={styles.sizeAfter}>{formatFileSize(item.fileSize)}</span>{item.compressSec && <span style={styles.compressTime}> ({item.compressSec}s)</span>}</>
                        : formatFileSize(item.fileSize)
                      }
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const styles = {
  pageTitle: {
    margin: "0 0 20px",
    fontSize: 22,
    fontWeight: 700,
    color: "#1e1e2d",
  },
  empty: {
    textAlign: "center",
    padding: "80px 20px",
  },
  sessionCard: {
    background: "#fff",
    borderRadius: 14,
    padding: "16px 20px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
    marginBottom: 16,
    position: "relative",
    overflow: "hidden",
  },
  sessionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 10,
  },
  sessionTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#1e1e2d",
    marginBottom: 2,
  },
  sessionMeta: {
    fontSize: 11,
    color: "#888",
  },
  dismissBtn: {
    background: "none",
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 12,
    color: "#888",
    cursor: "pointer",
  },
  cancelBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "none",
    border: "1px solid #e53935",
    borderRadius: 6,
    padding: "4px 12px",
    fontSize: 12,
    color: "#e53935",
    cursor: "pointer",
    fontWeight: 600,
  },
  confirmOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 5,
    background: "rgba(0,0,0,0.3)",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  confirmBox: {
    background: "#fff",
    borderRadius: 12,
    padding: "24px 20px",
    width: "80%",
    maxWidth: 300,
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
  },
  confirmKeepBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: 6,
    border: "1px solid #ddd",
    background: "#fff",
    fontSize: 12,
    cursor: "pointer",
    color: "#333",
  },
  confirmCancelBtn: {
    flex: 1,
    padding: "8px 12px",
    borderRadius: 6,
    border: "none",
    background: "#e53935",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  overallBarWrap: {
    height: 5,
    background: "#f0f0f0",
    borderRadius: 3,
    overflow: "hidden",
    marginBottom: 10,
  },
  overallBarFill: {
    height: "100%",
    borderRadius: 3,
    transition: "width 0.3s ease",
  },
  fileList: {
    maxHeight: 320,
    overflowY: "auto",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    marginTop: 8,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    borderBottom: "1px solid #f5f5f5",
    fontSize: 12,
  },
  fileIcon: { fontSize: 13, flexShrink: 0 },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: {
    display: "block",
    color: "#333",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginBottom: 2,
  },
  barWrap: {
    height: 4,
    background: "#f0f0f0",
    borderRadius: 2,
    overflow: "hidden",
    marginTop: 4,
    marginBottom: 2,
  },
  barFill: {
    height: "100%",
    borderRadius: 2,
    transition: "width 0.2s ease",
  },
  statusLabel: {
    fontSize: 11,
    color: "#888",
    display: "block",
    marginTop: 2,
  },
  fileSize: {
    color: "#aaa",
    flexShrink: 0,
    fontSize: 11,
    minWidth: 44,
    textAlign: "right",
  },
  fileSizeCompressed: {
    flexShrink: 0,
    fontSize: 11,
    minWidth: 44,
    textAlign: "right",
    color: "#aaa",
  },
  sizeArrow: {
    color: "#bbb",
  },
  sizeAfter: {
    color: "#16a34a",
    fontWeight: 700,
  },
  compressTime: {
    color: "#9ca3af",
    fontWeight: 400,
  },
  activeFileMini: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 10px",
    background: "#f9f8ff",
    borderRadius: 6,
    marginBottom: 6,
    overflow: "hidden",
  },
  activeFileName: {
    fontSize: 12,
    fontWeight: 600,
    color: "#333",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
    minWidth: 0,
  },
  activeFileStatus: {
    fontSize: 11,
    color: "#6c63ff",
    fontWeight: 600,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  toggleBtn: {
    background: "#f5f3ff",
    border: "1px solid #e0daf7",
    fontSize: 12,
    color: "#6c63ff",
    cursor: "pointer",
    padding: "6px 14px",
    fontWeight: 600,
    width: "100%",
    textAlign: "center",
    borderRadius: 6,
    marginTop: 4,
  },
};

export default RunningUploads;
